import { Tooltip } from '@material-ui/core'

import { DateUtils } from 'react-frontend-utils'
import { JournalEntry } from './JournalEntry'

import { ThemeColors } from '../Theme'
import { Global } from './Global'

import { Field, Constants, getGeneratedCalculationErrors } from 'form-builder'

const DATE_WHEN_RESYNC_AVAILABLE = new Date("2024-06-21T00:00:00Z");

const BACKLOG_OVERDUE_HRS = 20;

export class Application {

    static State = {

        CREATED:        {enumName: "CREATED", bit: 0, label: "Created", backgroundColor: ThemeColors.cancelGray},  
        SUBMITTED:      {enumName: "SUBMITTED", bit: 1, label: "Submitted", backgroundColor: ThemeColors.submittedBlue},
        CLAIMED:        {enumName: "CLAIMED", bit: 2, label: "Claimed", backgroundColor: ThemeColors.claimedPurple},
        ON_HOLD:        {enumName: "ON_HOLD", bit: 3, label: "On Hold", backgroundColor: ThemeColors.onHoldOrange},
        PROCESSED:      {enumName: "PROCESSED", bit: 4, label: "Processed", backgroundColor: ThemeColors.processedGreen},
        DECLINED:       {enumName: "DECLINED", bit: 5, label: "Declined", backgroundColor: ThemeColors.declinedRed}          
    };

    static PaymentPlanStatus = {
        CREATED:   {enumName: "CREATED"},
        ACTIVE:    {enumName: "ACTIVE"},
        COMPLETE:  {enumName: "COMPLETE"},
        CANCELLED: {enumName: "CANCELLED"},
        FAILED:    {enumName: "FAILED"}
    };
    
    static InsuranceStatus = {
        NOT_OFFERED:        {enumName: "NOT_OFFERED", label: "Not Offered", description: "Insurance was not offered to the patron", backgroundColor: 'white', color: ThemeColors.darkGray},
        DECLINED:           {enumName: "DECLINED", label: "Declined", description: "The patron was offered insurance but declined it", backgroundColor: ThemeColors.declineBrown, color: 'white'},
        ACCEPTED:           {enumName: "ACCEPTED", label: "Accepted", description: "The patron accepted insurance and the purchase is pending", backgroundColor: 'yellow', color: 'black'},
        PURCHASED:          {enumName: "PURCHASED", label: "Purchased", description: "Insurance was successfully purchased", backgroundColor: 'green', color: 'white'},
        FAILED:             {enumName: "FAILED", label: "Failed", description: "The patron accepted insurance but the purchase failed", backgroundColor: 'red', color: 'white'},
        CANCEL_REQUESTED:   {enumName: "CANCEL_REQUESTED", label: "Cancel Requested", description: "A request to cancel the insurance policy has been made", backgroundColor: ThemeColors.onHoldOrange, color: 'black'},
        CANCELLED:          {enumName: "CANCELLED", label: "Cancelled", description: "The insurance policy was cancelled", backgroundColor: 'red', color: 'white'}
    };
    
      
    id;
    type;               //Class type, for Jackson
    isCopy;

    submitDate;         //Converted to local
    patronTzOffset;     //Patron timezone, in min west
    patronIPAddress;
    submitterEmail;       
    group;      
    currency;
    calculatedPaymentAmount;
    calculatedFutureBalanceAmount;

    paymentIntent;
    paidOn;
    refundedAmount;
    originallyReviewedBy;
    syncedMembership;

    testing;
    
    stateEnum;
    previousStateEnum;
    agsEscalation;
    isFullService;

    isSupportRequest;
    originalApplicationID;
    isPaper;
    isRenewal;
    renewalMembershipID;
    paperPaymentAmount;
    checkInfo;

    claimedBy;
    claimerName;
    claimedAt;          //Converted to local
    fetchDate;

    fieldTree;
    fieldCalculations;
    constants;

    hmac;
    journal;
  
    instructions;
    hasMerge;
    canSyncWithPP;
    communityURL;
    extendedCommunityName;

    hasFlag;  //internal

    hasPaymentPlan;
    numInstallmentsRemain;
    paymentPlanStatus;
    paymentPlanPaid;

    insuranceStatus;

    constructor(json, forPatron = false) {
        if (json) {
            
            this.id = json.id;
            this.type = json.type;
            this.isCopy = json.isCopy;
            if (json.submitDate) {
                this.submitDate = DateUtils.parseJsonDate(json.submitDate, true); //convert from UTC
            }
            if (json.patronTzOffset)
                this.patronTzOffset = json.patronTzOffset;
            else
                this.patronTzOffset = 0;
            
            this.patronIPAddress = json.patronIPAddress;
            this.submitterEmail = json.submitterEmail;
            this.group = json.group;
            this.testing = json.testing;
            this.currency = json.currency;
            this.calculatedPaymentAmount = json.calculatedPaymentAmount;
            this.calculatedFutureBalanceAmount = json.calculatedFutureBalanceAmount;
            this.paymentIntent = json.paymentIntent;

            if (json.paidOn)
                this.paidOn = DateUtils.parseJsonDate(json.paidOn, true); //convert from UTC

            this.refundedAmount = json.refundedAmount;

            this.originallyReviewedBy = json.originallyReviewedBy;
            this.syncedMembership = json.syncedMembership;

            if (json.state) {
                this.stateEnum = Application.stateFromString(json.state);    
            }
            if (json.previousState)
                this.previousStateEnum = Application.stateFromString(json.previousState);
            
            this.agsEscalation = json.agsEscalation;
            this.isFullService = json.isFullService;
            this.isSupportRequest = json.isSupportRequest;
            this.originalApplicationID = json.originalApplicationID;
            this.isPaper = json.isPaper;
            this.isRenewal = json.isRenewal;
            this.renewalMembershipID = json.renewalMembershipID;
            this.paperPaymentAmount = json.paperPaymentAmount;
            this.checkInfo = json.checkInfo;

            this.hasPaymentPlan = json.hasPaymentPlan;
            this.paymentPlanPaid = json.paymentPlanPaid;
            this.numInstallmentsRemain = json.numInstallmentsRemain;
            if (json.paymentPlanStatus)
                this.paymentPlanStatus = Application.paymentPlanStatusFromString(json.paymentPlanStatus);

            this.insuranceStatus = json.insuranceStatus ? Application.insuranceStatusFromString(json.insuranceStatus) : Application.InsuranceStatus.NOT_OFFERED;

            this.claimedBy = json.claimedBy;
            this.claimerName = json.claimerName;
            if (json.claimedAt) {
                this.claimedAt = DateUtils.parseJsonDate(json.claimedAt, true); //convert from UTC
            }
            else
                this.claimedAt = null;

            this.fetchDate = json.fetchDate;  //don't convert to date, this should be passed back to the backend unchanged
            
            this.hmac = json.hmac;
            if (json.journal) {
                this.journal = json.journal.map(entry => new JournalEntry(entry));        
            }
            else
                this.journal = []; 

            for (const entry of this.journal) {
                if (entry.activity.startsWith("Flagged to")) {
                    this.hasFlag = true;
                    break;
                }
            }
            
            this.instructions = json.instructions;
            this.hasMerge = json.hasMerge;
            this.canSyncWithPP = json.canSyncWithPP;
            this.communityURL = json.communityURL;
            this.extendedCommunityName = json.extendedCommunityName;
            
            if (json.fieldTree) {  //only populated when individual Application is pulled
                this.fieldTree = json.fieldTree;
                this.fieldTree.isBase = true;           //mark this as the base of the tree
            }
            
            this.fieldCalculations = json.fieldCalculations;
            this.constants = new Constants(json.constants);
                        
        }
    }


    claimable() {
        switch (this.stateEnum) {
            case Application.State.PROCESSED:
                if (this.submitDate > DATE_WHEN_RESYNC_AVAILABLE)
                    return true;
                else
                    return false;
            case Application.State.SUBMITTED:
            case Application.State.ON_HOLD:
            case Application.State.DECLINED:
                return true;
               
            default:
                return false;
        }
    }

    escalateable() {
        switch (this.stateEnum) {  //these states cannot be escalated
            case Application.State.PROCESSED:
            case Application.State.CREATED:
                return false;
            default:
                break;
        }

        return !this.agsEscalation;  //can escalate if we are not escalated
    }
    
    claimedByUser() {
        return this.stateEnum === Application.State.CLAIMED && this.claimedBy === Global.user.id;
    }
    
    isTooLarge() {
        const json = this.toJsonForPost();
        if (json.length > 15 * 1024 * 1024)  //15 MB
            return true;
        else
            return false;
    }
    
    isMissingData() {
        return Field.fieldMissingData(this.fieldTree);
    }

    hasErroredField() {
        return Field.fieldErrored(this.fieldTree);
    }

    getGeneratedErrors() {
        return getGeneratedCalculationErrors(this.fieldTree, this.fieldCalculations, this.constants);
    }

    // Only for employees, if it's older than 20 hours and still in SUBMITTED state, for full service communities
    isLateForProcessing() {     
        if (!Global.user.isSiteEmployee())
            return false;

        if (this.stateEnum !== Application.State.SUBMITTED || !this.isFullService || this.isSupportRequest)
            return false;
        
        const now = new Date();
        const diff = now - this.submitDate;
        const hours = diff / (1000 * 3600);
        return hours > BACKLOG_OVERDUE_HRS;
    }


    //Conver to JSON for posting, removes items that are not allowed by backend
    toJsonForPost() {
        
        this.id = null; 
        this.submitDate = null; //will not be used by backend      
        this.stateEnum = null;  //not used by backend
        this.previousStateEnum = null;  //not used by backend
        this.journal = null; 
        this.insuranceStatus = null;
        this.paymentPlanStatus = null;
                
        return JSON.stringify(this);
    }
    
    
    static stateFromString(str) {
        for (let index in Application.State) {
            const state = Application.State[index];

            if (state.enumName === str) {
                return state;
            }
        }  
        return null;
    }

    static paymentPlanStatusFromString(str) {
        for (let index in Application.PaymentPlanStatus) {
            const status = Application.PaymentPlanStatus[index];

            if (status.enumName === str) {
                return status;
            }
        }  
        return null;
    }

    static insuranceStatusFromString(str) {
        for (let index in Application.InsuranceStatus) {
            const status = Application.InsuranceStatus[index];

            if (status.enumName === str) {
                return status;
            }
        }  
        return null;
    }
    
    
    //Render the Journal Entries
    renderJournal() {
        
        const stateChangeActivity = (entry) => {
            if (entry.activity.startsWith("Released to ") || entry.activity.startsWith("Force released to ")) {
                const prefix = entry.activity.startsWith("Force released to ") ? "Force released to " : "Released to ";
                const stateReleased = Application.stateFromString(entry.activity.substring(prefix.length));
            
                return <div style={{display: 'flex'}}>
                            {prefix}
                            <div style={{marginLeft: 5, paddingLeft: 3, paddingRight: 5, backgroundColor: stateReleased.backgroundColor, color: 'white', textTransform: 'uppercase'}}>
                                {stateReleased.label}
                            </div>
                        </div>;
            }
            else
                return <div>{entry.activity}</div>;
        };
        
        const flag = (entry) => {
            if (entry.activity.startsWith("Flagged to")) 
                return <div style={{fontSize: 24, color: 'blue', marginTop: -6, marginLeft: 10, padding: -4}}>⚑</div>         
            else
                return null;
        }

        const escalated = (entry) => {
            if (entry.activity.startsWith("Escalated to Access Granted Support"))
                return <div style={{fontSize: 14, fontWeight: 'bold', marginLeft: 7}}>!</div>         
            else
                return null;
        }
        
        return (<div style={{marginLeft: 40, marginRight: 40}}>
                    {this.journal.map((entry, index) => {
                        return (
                            <div key={index} style={{backgroundColor: ThemeColors.notifyBackground, marginBottom: 2, padding: 5, borderRadius: 3}}>
                                <div style={{display: 'flex'}}>
                                    <div style={{width: 200}}>{DateUtils.timeFormat(entry.date)}</div>
                                    <div style={{width: 200}}>{entry.author}</div>
                                    {stateChangeActivity(entry)}
                                    {flag(entry)}
                                    {escalated(entry)}
                                </div>
                                <div style={{fontSize: 12, marginLeft: 400}}>{entry.note ? entry.note : null}</div>
                            </div>
                        );
                    })}
                </div>);
    }

    static badgeStyle = {
        testing: {
            alignContent: 'center',
            width: 14,
            textTransform: 'uppercase',
            padding: 2, 
            borderRadius: '50%', 
            color: 'white', 
            textAlign: 'center',
            fontSize: 12,
            height: 14,
        },
        renewal: {
            alignContent: 'center',
            width: 14,
            textTransform: 'uppercase',
            padding: 1, 
            borderRadius: '20%', 
            color: 'white', 
            textAlign: 'center',
            height: 14,
            fontSize: 12,
            marginLeft: 2,
            marginRight: 2, 
            transform: 'rotate(45deg)',
            position: 'relative',
            top: 1
        },
        copy: {
            alignContent: 'center',
            width: 14,
            textTransform: 'uppercase',
            padding: 2, 
            borderRadius: '50%', 
            color: 'black', 
            textAlign: 'center',
            fontSize: 12,
            height: 14
        },
        flag: {
            alignContent: 'center',
            width: 14,
            borderRadius: '50%', 
            paddingLeft: 2,
            paddingRight: 2, 
            textAlign: 'center',
            fontSize: 14, 
            color: 'white',
            height: 18
        },
        escalate: {
            alignContent: 'center',
            width: 14,
            borderRadius: '50%', 
            paddingLeft: 2,
            paddingRight: 2, 
            textAlign: 'center',
            fontSize: 14, 
            fontWeight: 'bold',
            color: 'white',
            height: 18
        },
        supportRequest: {
            alignContent: 'center',
            borderRadius: '30%', 
            paddingLeft: 2,
            paddingRight: 3, 
            textAlign: 'center',
            color: 'white',
        },
        fullService: {
            alignContent: 'center',
            borderRadius: '10%', 
            paddingLeft: 3,
            paddingRight: 3, 
            textAlign: 'center',
            fontSize: 14,
            fontWeight: 'bold', 
            color: 'white',
            height: 18
        },
        paper: {
            alignContent: 'center',
            paddingLeft: 0,
            paddingRight: 0, 
            textAlign: 'center',
            transform: 'rotate(80deg)  scale(1.35) ',
            fontSize: 12,
            fontWeight: 'bold', 
            position: 'relative',
        }
    };


    static renderSupportRequestBadge(application, largeFont = false) {
        return  <Tooltip title={application.isSupportRequest ? "This is a support request" : ""}>
                        <div style={{...Application.badgeStyle.supportRequest, fontSize: largeFont ? 18 : 14, height: largeFont ? 22 : 18, backgroundColor: application.isSupportRequest ? ThemeColors.helpCyan : 'white'}}>
                            {application.isSupportRequest ? "⁈" : ""}
                        </div>
                </Tooltip>
    }

    static renderRenewalBadge(application, largeFont = false) {
        const transform = largeFont ? 'scale(1.15)' + Application.badgeStyle.renewal.transform + ' translateY(1.3px)' : Application.badgeStyle.renewal.transform;
        return  <Tooltip title="This is an renewal application">
                        <div style={{...Application.badgeStyle.renewal, transform: transform, marginBottom: 5, backgroundColor: application.isRenewal ? ThemeColors.renewalGreen : 'white'}}>
                            <span style={{display: 'inline-block', transform: 'rotate(-45deg)'}}>{application.isRenewal ? "R" : ""}</span>
                        </div>
                </Tooltip>
    }



    renderBadges(justify = 'left', forList = true) {

        let fullServiceColor = 'white';   // initially, not full service
        let fullServiceTooltip = "";
        if (this.isFullService) {
            fullServiceColor = 'gray';
            fullServiceTooltip = "This belongs to a full service community";
        }
        if (this.isLateForProcessing()) {
            fullServiceColor = 'orange';
            fullServiceTooltip = "Full service community Application that is overdue for processing";
        }

        return <div style={{display: 'flex', justifyContent: justify, gap: 3}}>
                    <Tooltip title={fullServiceTooltip}>
                        <div style={{...Application.badgeStyle.fullService, backgroundColor: fullServiceColor}}>
                            {this.isFullService ? "F" : ""}
                        </div>
                    </Tooltip>
                    {forList ? Application.renderSupportRequestBadge(this) : null}
                    <Tooltip title={this.hasFlag ? "This has been flagged as needing attention from the Community" : ""}>
                        <div style={{...Application.badgeStyle.flag, backgroundColor: this.hasFlag ? 'blue' : 'white'}}>
                            {this.hasFlag ? "⚑" : ""}
                        </div>
                    </Tooltip>
                    <Tooltip title={this.agsEscalation ? "This has been escalated to Access Granted support" : ""}>
                        <div style={{...Application.badgeStyle.escalate, backgroundColor: this.agsEscalation ? 'black' : 'white'}}>
                            {this.agsEscalation ? "!" : ""}
                        </div>
                    </Tooltip>
                    <Tooltip title={this.isPaper ? "This is a paper submission" : ""}>
                        <div style={{...Application.badgeStyle.paper}}>
                            {this.isPaper ? "✏️" : ""}
                        </div>
                    </Tooltip>
                    {Application.renderRenewalBadge(this)}
                    <Tooltip title={this.testing ? "This is a test submission" : ""}>
                        <div style={{...Application.badgeStyle.testing, backgroundColor: this.testing ? 'red' : 'white'}}>
                            {this.testing ? "T" : ""}
                        </div>
                    </Tooltip>
                    <Tooltip title={this.isCopy ? "This is a copy" : ""}>
                        <div style={{...Application.badgeStyle.copy, backgroundColor: this.isCopy ? 'orange' : 'white'}}>
                            {this.isCopy ? "C" : ""}
                        </div>
                    </Tooltip>
                </div>;
    }
    
    


}
